<template>
  <div>
    <h2 class="text-xl font-semibold mb-2">Generate Mailboxes</h2>
    <p class="text-sm secondary_color mb-6">
      Select domains and specify the number of mailboxes for each
    </p>

    <div class="mb-8 flex flex-col gap-2 w-[50%]" ref="domainsSection">
      <label class="text-sm secondary_color">Domains</label>
      <SearchableMultiSelectDropdown
        v-model="selectedDomains"
        :options="availableDomains"
        placeholder="Select domains"
        custom-class="w-full"
        :match-trigger-width="true"
        search-title="Choose Domains"
        search-placeholder="Search domains"
        cta-text="Generate New Domains"
        :has-error="!!validationErrors.domains"
        @cta-click="handleGenerateDomainsClick"
        @selection-change="handleDomainsChange"
      />
      <div v-if="validationErrors.domains" class="error_message_color text-xs">
        {{ validationErrors.domains }}
      </div>

      <!-- <p
        v-if="availableDomains.length !== 0"
        class="text-sm cursor-pointer w-fit"
        @click="toggleAllDomains"
      >
        {{ isAllSelected ? "Unselect all" : "Select all" }}
      </p> -->
    </div>

    <div class="mb-10 w-[50%]" ref="mailboxesSection">
      <label class="block text-sm mb-2 secondary_color"
        >Number of mailboxes to generate per selected domain</label
      >
      <div
        class="relative"
        :class="{ 'has-error': validationErrors.mailboxes }"
      >
        <a-input-number
          v-model:value="mailboxesPerDomain"
          :min="1"
          :max="maxMailboxes"
          class="w-full"
          :controls="false"
          placeholder="Enter number of mailboxes"
          @blur="validateMailboxesOnBlur"
          @input="handleMailboxesInput"
        />
        <span class="absolute right-3 top-2 secondary_color text-xs"
          >max {{ maxMailboxes }}</span
        >
      </div>
      <div
        v-if="validationErrors.mailboxes"
        class="error_message_color text-xs mt-1"
      >
        {{ validationErrors.mailboxes }}
      </div>
      <div class="text-sm mt-2 secondary_color">
        Total mailboxes to be generated:
        {{ selectedDomains.length * mailboxesPerDomain }}
      </div>
    </div>

    <div class="mb-8">
      <h2 class="text-xl font-semibold mb-2">Mailbox Names</h2>
      <p class="text-sm secondary_color mb-6">
        Select name for generated mailboxes.
      </p>

      <label class="block text-sm mb-2 secondary_color flex items-center gap-1">
        Name for the generated mailboxes
        <a-tooltip placement="top">
          <template #title>
            Select the names of the generated mailboxes that will appear for
            recipients in their Inbox. You can select Random Male and/or Female
            names for the mailboxes. Names will be assigned automatically and
            can be changed manually.
          </template>

          <ThemeImage
            :icon="imageNames.INFO"
            alt="info_icon"
            class="size-4 ml-1"
          />
        </a-tooltip>
      </label>
      <div class="w-[50%]">
        <a-select
          allowClear
          showSearch
          v-model:value="nameType"
          placeholder="Select name type"
          class="w-full"
          :options="nameTypeOptions"
        >
          <template #suffixIcon>
            <img
              :src="imagePath.dark.arrows.bottomArrowWhite"
              alt="arrow"
              class="w-4 h-4"
            />
          </template>
        </a-select>
      </div>
    </div>

    <div class="mb-8">
      <div
        class="flex items-center mb-4 cursor-pointer"
        @click="toggleSignatureSection"
      >
        <div class="flex items-center gap-2">
          <div
            class="transform transition-transform container_8 p-1.5 rounded-lg"
            :class="{ 'rotate-90': showSignatureSection }"
          >
            <ThemeImage
              :icon="imageNames.RIGHT_ARROW"
              alt="arrow"
              class="w-4 h-4"
            />
          </div>
          <div>
            <h2 class="text-lg">Signature & Forwarding</h2>
            <p class="text-sm secondary_color">
              Fill out signature for your mailboxes
            </p>
          </div>
        </div>
      </div>

      <div v-if="showSignatureSection" class="px-1 py-4">
        <div class="flex flex-row gap-4 mb-6">
          <div class="w-full">
            <SignatureEditor v-model="localSignature" :height="340" />
          </div>
        </div>

        <div
          class="mt-6 flex flex-col gap-2 w-[50%]"
          ref="forwardingEmailSection"
        >
          <label class="secondary_color text-sm flex items-center gap-1">
            Forwarding email
            <a-tooltip placement="top">
              <template #title>
                All emails sent to these mailboxes will be forwarded to this
                email. It is recommended to create filters to prevent your inbox
                from being flooded.
              </template>
              <ThemeImage
                :icon="imageNames.INFO"
                alt="info_icon"
                class="size-4 mr-1"
              />
            </a-tooltip>
          </label>
          <a-input
            v-model:value="localForwardingEmail"
            type="email"
            class="w-full input_medium"
            placeholder="Enter forwarding email address"
            :status="validationErrors.forwardingEmail ? 'error' : ''"
            @blur="validateForwardingEmailOnBlur"
            @input="handleForwardingEmailInput"
          />
          <div
            v-if="validationErrors.forwardingEmail"
            class="error_message_color text-xs"
          >
            {{ validationErrors.forwardingEmail }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits, watch, computed } from "vue";
import { message } from "ant-design-vue";
import imagePath from "@/src/paths/imagePath.js";
import SignatureEditor from "@/src/components/signature_editor/SignatureEditor.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { NAME_TYPE_OPTIONS } from "@/src/constants/mailboxesConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import SearchableMultiSelectDropdown from "@/src/components/SearchableMultiSelectDropdown.vue";
import { validateEmail } from "@/src/utils/utils.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  storedDomains: {
    type: Array,
    default: () => [],
  },
  storedMailboxesPerDomain: {
    type: Number,
    default: 5,
  },
  storedNameType: {
    type: String,
    default: "combo",
  },
  storedSignature: {
    type: String,
    default: "",
  },
  storedForwardingEmail: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:domains",
  "update:mailboxesPerDomain",
  "update:nameType",
  "update:signature",
  "update:forwardingEmail",
]);

const availableDomains = ref([]);
const selectedDomains = ref([]);
const mailboxesPerDomain = ref(5);
const maxMailboxes = ref(5);
const nameType = ref("combo");
const nameTypeOptions = ref(NAME_TYPE_OPTIONS);

const showSignatureSection = ref(true);
const localSignature = ref("");
const localForwardingEmail = ref("");
const handleForwardingEmailInput = () => {
  if (!validationErrors.value.forwardingEmail || !showSignatureSection.value)
    return;

  const email = localForwardingEmail.value?.trim();

  if (!email) {
    validationErrors.value.forwardingEmail = "Forwarding email is required";
  } else if (!validateEmail(email)) {
    validationErrors.value.forwardingEmail = "Email is not valid";
  } else {
    validationErrors.value.forwardingEmail = "";
  }
};

const domainsSection = ref(null);
const forwardingEmailSection = ref(null);
const mailboxesSection = ref(null);

const validationErrors = ref({
  domains: "",
  mailboxes: "",
  forwardingEmail: "",
});

const isAllSelected = computed(() => {
  return selectedDomains.value.length === availableDomains.value.length;
});

const validateDomains = () => {
  if (!selectedDomains.value || selectedDomains.value.length === 0) {
    validationErrors.value.domains = "Please select at least one domain";
    return false;
  }
  validationErrors.value.domains = "";
  return true;
};

const validateMailboxes = () => {
  if (!mailboxesPerDomain.value || mailboxesPerDomain.value < 1) {
    validationErrors.value.mailboxes = "Please enter at least 1 mailbox";
    return false;
  }
  validationErrors.value.mailboxes = "";
  return true;
};

const validateMailboxesOnBlur = () => {
  validateMailboxes();
};

const handleMailboxesInput = () => {
  clearValidationError("mailboxes");
};

const validateForwardingEmail = () => {
  if (showSignatureSection.value) {
    if (
      !localForwardingEmail.value ||
      localForwardingEmail.value.trim() === ""
    ) {
      validationErrors.value.forwardingEmail = "Forwarding email is required";
      return false;
    }
    if (!validateEmail(localForwardingEmail.value)) {
      validationErrors.value.forwardingEmail =
        "Please enter a valid email address";
      return false;
    }
  }
  validationErrors.value.forwardingEmail = "";
  return true;
};

const validateForwardingEmailOnBlur = () => {
  if (!showSignatureSection.value) return;

  const email = localForwardingEmail.value?.trim();

  if (!email) {
    validationErrors.value.forwardingEmail = "Forwarding email is required";
    return;
  }

  if (!validateEmail(email)) {
    validationErrors.value.forwardingEmail = "Email is not valid";
    return;
  }

  validationErrors.value.forwardingEmail = "";
};

const validateForm = () => {
  const isDomainsValid = validateDomains();
  const isMailboxesValid = validateMailboxes();
  const isForwardingEmailValid = validateForwardingEmail();

  const allValid = isDomainsValid && isMailboxesValid && isForwardingEmailValid;

  if (!allValid) {
    scrollToFirstError();
  }

  return allValid;
};

const scrollToFirstError = () => {
  const errorFields = [
    { hasError: validationErrors.value.domains, ref: domainsSection },
    { hasError: validationErrors.value.mailboxes, ref: mailboxesSection },
    {
      hasError: validationErrors.value.forwardingEmail,
      ref: forwardingEmailSection,
    },
  ];

  const firstError = errorFields.find((field) => field.hasError);

  if (firstError && firstError.ref.value) {
    if (
      firstError.hasError === validationErrors.value.forwardingEmail &&
      !showSignatureSection.value
    ) {
      showSignatureSection.value = true;
      setTimeout(() => {
        firstError.ref.value.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    } else {
      firstError.ref.value.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }
};

const clearValidationError = (field) => {
  if (validationErrors.value[field]) {
    validationErrors.value[field] = "";
  }
};

const getSelectedDomains = () => {
  return selectedDomains.value;
};

const getForwardingEmail = () => {
  return localForwardingEmail.value;
};

defineExpose({
  validateForm,
  validateBeforeNext: validateForm,
  getSelectedDomains,
  getForwardingEmail,
});

const handleDomainsChange = (newDomains) => {
  selectedDomains.value = newDomains;
  clearValidationError("domains");
  emit("update:domains", newDomains);
};

const toggleAllDomains = () => {
  if (isAllSelected.value) {
    selectedDomains.value = [];
  } else {
    selectedDomains.value = availableDomains.value.map(
      (domain) => domain.value,
    );
  }
  clearValidationError("domains");
  emit("update:domains", selectedDomains.value);
};

const toggleSignatureSection = () => {
  showSignatureSection.value = !showSignatureSection.value;
};

const handleGenerateDomainsClick = () => {
  window.open("/generate-domains", "_blank");
};

watch(mailboxesPerDomain, (newValue) => {
  emit("update:mailboxesPerDomain", newValue);
});

watch(nameType, (newValue) => {
  emit("update:nameType", newValue);
});

watch(localSignature, (newValue) => {
  emit("update:signature", newValue);
});

watch(localForwardingEmail, (newValue) => {
  clearValidationError("forwardingEmail");
  emit("update:forwardingEmail", newValue);
});

onMounted(async () => {
  if (props.storedDomains && props.storedDomains.length > 0) {
    selectedDomains.value = props.storedDomains;
  }

  if (props.storedMailboxesPerDomain) {
    mailboxesPerDomain.value = props.storedMailboxesPerDomain;
  }

  if (props.storedNameType) {
    nameType.value = props.storedNameType;
  }

  if (props.storedSignature) {
    localSignature.value = props.storedSignature;
  } else {
    localSignature.value =
      "Best,\n{{contact.firstname | default: 'there'}} {{contact.lastname | default: ''}}";
  }

  if (props.storedForwardingEmail) {
    localForwardingEmail.value = props.storedForwardingEmail;
  }

  try {
    const domains = await DashboardService.getAllDomains();
    availableDomains.value =
      domains?.data?.map((domain) => {
        const fullDomain = `${domain?.sld}.${domain?.tld}`;
        return {
          value: fullDomain,
          label: fullDomain,
        };
      }) || [];
  } catch (error) {
    console.error("Error fetching domains:", error);
    message.error("Failed to fetch domains");
  }
});
</script>

<style scoped>
.has-error :deep(.ant-input-number) {
  border-color: var(--error-color) !important;
}

.has-error :deep(.ant-input-number:hover),
.has-error :deep(.ant-input-number:focus) {
  border-color: var(--error-color) !important;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}
</style>
